import { useCallback, useRef, useState } from "react";
import { buildGazDeliveryMaterial, fetchBrickDeliveryCost } from "./brickDeliveryPricing";

const buildKey = (name, quantity, selectedRegion, address, paymentMethod) =>
  `${name}_${quantity}_${selectedRegion || ""}_${address || ""}_${paymentMethod}`;

// Считает доставку для аналогов кирпича лениво (по требованию раскрытой строки),
// а не для всей номенклатуры сразу — иначе на каждый рендер таблицы товаров
// улетали бы десятки параллельных запросов на /api/delivery_gaz.
const useBrickAnalogDelivery = ({ addressAndCoordinates, selectedRegion, selfPickUp, deliveryMethod, paymentMethod }) => {
  const [deliveryByKey, setDeliveryByKey] = useState({});
  const inFlightKeys = useRef(new Set());

  const requestDelivery = useCallback((name, data, quantity) => {
    if (!name || !data || !quantity) return;

    const key = buildKey(name, quantity, selectedRegion, addressAndCoordinates?.address, paymentMethod);
    if (deliveryByKey[key] || inFlightKeys.current.has(key)) return;

    if (selfPickUp) {
      setDeliveryByKey((prev) => ({ ...prev, [key]: { status: "ok", perUnit: 0 } }));
      return;
    }

    if (!addressAndCoordinates?.coordinates) {
      setDeliveryByKey((prev) => ({ ...prev, [key]: { status: "error" } }));
      return;
    }

    inFlightKeys.current.add(key);
    setDeliveryByKey((prev) => ({ ...prev, [key]: { status: "loading" } }));

    const material = buildGazDeliveryMaterial(name, data, quantity);
    fetchBrickDeliveryCost({ material, addressAndCoordinates, selectedRegion, deliveryMethod, paymentMethod })
      .then(({ totalCost, error }) => {
        inFlightKeys.current.delete(key);
        setDeliveryByKey((prev) => ({
          ...prev,
          [key]: error ? { status: "error" } : { status: "ok", perUnit: totalCost / quantity },
        }));
      })
      .catch(() => {
        inFlightKeys.current.delete(key);
        setDeliveryByKey((prev) => ({ ...prev, [key]: { status: "error" } }));
      });
  }, [addressAndCoordinates, selectedRegion, selfPickUp, deliveryMethod, paymentMethod, deliveryByKey]);

  const getDeliveryState = useCallback((name, quantity) => {
    const key = buildKey(name, quantity, selectedRegion, addressAndCoordinates?.address, paymentMethod);
    return deliveryByKey[key] || null;
  }, [deliveryByKey, selectedRegion, addressAndCoordinates, paymentMethod]);

  return { requestDelivery, getDeliveryState };
};

export default useBrickAnalogDelivery;
